import React from 'react'
import classnames from 'classnames/bind'
import InfiniteScroll from 'react-infinite-scroller'
import { useRouter } from 'next/router'
import { isNull } from 'lodash-es'

// Libs
import useMedia from '@/hooks/useMedia'
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { handleGtmViewItemList } from '@/methods/handleGtmEvent'
import { useObserver } from '@/hooks/useObserver'

// Components
import ProductCard from '@/components/shared/ProductCard'
import Empty from '@/components/shared/Empty'
import HotLiveList from '@/components/shared/HotLiveList'
import ProductCardSkeleton from '@/components/shared/ProductCard/skeleton/ProductCardSkeleton'
import VirtualizedMallList from './components/VirtualizedMallList'

// Constants
import { MALL_DEFAULT_PAGE_SIZE } from '@/constants/mall'
import { ProductInfoType } from '@/api/fetchProductData'
import { ROUTE_PATH } from '@/constants/routePath'

// Styles
import styles from './style.module.scss'

const cx = classnames.bind(styles)

type TProductCardDataAttributes = {
  dataset: {
    id: string
  }
} & Element

type PopularProductListProps = {
  className?: string
  popularListData: any
  gtmItemEventParams: {
    itemListName: string
    itemListId?: string
  }
}

const PopularProductList = (props: PopularProductListProps) => {
  const { className, popularListData, gtmItemEventParams } = props
  const { isMobile } = useMedia()
  const router = useRouter()
  const isMallPage = router.pathname === ROUTE_PATH.MALL
  const isVirtualized = isMobile && isMallPage
  const productList = popularListData?.data?.pages
    .map((item: { productsInfo: ProductInfoType[] }) => item.productsInfo)
    .flat()
    .filter((rowItem: { productsInfo: ProductInfoType[] }) => !isNull(rowItem))

  const isIntersecCb = (entries: IntersectionObserverEntry[]) => {
    const isIntersectingEntries = entries.filter((entry: IntersectionObserverEntry) => entry.isIntersecting)
    const dataAttributes = isIntersectingEntries.map((entry) => (entry.target as TProductCardDataAttributes).dataset)
    const productIdList = dataAttributes.map((item) => Number(item.id))
    if (productIdList.length > 0 && productList) {
      const intersectingProductList = productList.filter((productItem: ProductInfoType) => productIdList.includes(productItem.productId))
      handleGtmViewItemList({ ...gtmItemEventParams, productInfoList: intersectingProductList })
    }
  }

  const { observerRef } = useObserver({ observerCallback: isIntersecCb, dep: [popularListData.data] })
  const handleNextpage = () => {
    if (!popularListData.isFetchingNextPage) {
      popularListData.fetchNextPage()
    }
  }
  return (
    <div className={cx('mallList', className)}>
      {popularListData?.isSuccess && !popularListData?.data?.pages[0].productsInfo && <Empty />}

      {/* PopularProduct List */}
      {isVirtualized ? (
        <>
          {popularListData?.isSuccess && popularListData?.data?.pages.length > 0 && popularListData?.data?.pages[0].productsInfo && (
            <VirtualizedMallList observer={observerRef.current} categoryListData={popularListData} gtmItemEventParams={gtmItemEventParams} />
          )}
        </>
      ) : (
        <InfiniteScroll className={cx('mallList__list')} loadMore={handleNextpage} hasMore={popularListData.hasNextPage}>
          {!popularListData?.isSuccess && (
            <ProductCardSkeleton count={isMobile ? 6 : popularListData?.data?.pages.length * MALL_DEFAULT_PAGE_SIZE || 20} />
          )}
          {popularListData?.isSuccess &&
            popularListData?.data?.pages.length > 0 &&
            popularListData?.data?.pages.map((item: any, pageIndex: number) => (
              <React.Fragment key={pageIndex}>
                {typeof window !== undefined &&
                  item?.productsInfo?.map((item: any) => (
                    <ProductCard key={item.productId} {...item} observer={observerRef.current} gtmItemEventParams={gtmItemEventParams} />
                  ))}
              </React.Fragment>
            ))}
          {popularListData?.isFetchingNextPage && <ProductCardSkeleton count={14} />}
        </InfiniteScroll>
      )}

      {/* PopularProduct List */}

      {popularListData?.isSuccess && !popularListData?.data?.pages[0].productsInfo && <HotLiveList />}
    </div>
  )
}

export default PopularProductList
